import React from 'react';
import { useQuery } from 'react-query';
import { api } from '../../utils/api';

/*
    Pre-trip and post-trip vehicle photos, and the bills, of an ACTING trip.

    The acting driver records these on the trip (actingDetails.inspections.{pre,post}
    and actingDetails.bills), but the trip page had no section for them, so an admin
    opening a trip saw the booking and fare and nothing of the evidence behind it.
    The server (GET /trips/:id/acting-media) hands back temporary links for the private
    object-store images.
*/

const SLOT_LABELS = {
  FRONT: 'Front',
  REAR: 'Rear',
  LEFT: 'Left',
  RIGHT: 'Right',
  SELFIE_WITH_CAR: 'Selfie with car',
  ODOMETER: 'Odometer',
  DENT: 'Damage',
};

const INSPECTION_BADGE = {
  DRAFT: { label: 'Draft', cls: 'bg-gray-100 text-gray-800' },
  SUBMITTED: { label: 'Awaiting customer', cls: 'bg-blue-100 text-blue-800' },
  APPROVED: { label: 'Approved by customer', cls: 'bg-green-100 text-green-800' },
  REJECTED: { label: 'Rejected by customer', cls: 'bg-red-100 text-red-800' },
};

const BILL_BADGE = {
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-800' },
  pending: { label: 'Pending', cls: 'bg-yellow-100 text-yellow-800' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-800' },
};

const when = (ms) => (ms ? new Date(Number(ms)).toLocaleString() : '—');

const Badge = ({ badge, fallback }) => {
  const b = badge || { label: fallback || '—', cls: 'bg-gray-100 text-gray-800' };
  return <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${b.cls}`}>{b.label}</span>;
};

const InspectionBlock = ({ title, inspection }) => (
  <div>
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <h3 className="text-base font-medium text-gray-900">{title}</h3>
      {inspection && <Badge badge={INSPECTION_BADGE[inspection.status]} fallback={inspection.status} />}
    </div>

    {!inspection || inspection.photos.length === 0 ? (
      <p className="text-sm text-gray-500">No photos were uploaded.</p>
    ) : (
      <>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {inspection.photos.map((photo, index) => (
            <a
              key={`${photo.slot}-${index}`}
              href={photo.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block group"
              title="Open full size"
            >
              <img
                src={photo.url}
                alt={SLOT_LABELS[photo.slot] || photo.slot}
                loading="lazy"
                className="h-32 w-full object-cover rounded-md border border-gray-200 group-hover:opacity-90"
              />
              <p className="mt-1 text-xs text-gray-600">
                {SLOT_LABELS[photo.slot] || photo.slot}
                {photo.uploadedAt ? ` · ${when(photo.uploadedAt)}` : ''}
              </p>
            </a>
          ))}
        </div>
        <div className="mt-2 text-xs text-gray-500 space-y-0.5">
          {inspection.submittedAt ? <p>Submitted: {when(inspection.submittedAt)}</p> : null}
          {inspection.decidedAt ? <p>Customer decision: {when(inspection.decidedAt)}</p> : null}
          {inspection.status === 'REJECTED' && inspection.rejectionReason ? (
            <p className="text-red-600">Reason: {inspection.rejectionReason}</p>
          ) : null}
          {inspection.rejectionCount > 0 ? (
            <p>Earlier rejected submissions: {inspection.rejectionCount}</p>
          ) : null}
        </div>
      </>
    )}
  </div>
);

const ActingTripMedia = ({ tripId }) => {
  const { data, isLoading, error } = useQuery(
    ['acting-trip-media', tripId],
    () => api.get(`/trips/${tripId}/acting-media`),
    { enabled: !!tripId, retry: 1, refetchOnWindowFocus: false }
  );

  const media = data?.data?.data || data?.data || null;
  const bills = Array.isArray(media?.bills) ? media.bills : [];
  const approvedTotal = bills
    .filter((b) => b.approval === 'approved')
    .reduce((sum, b) => sum + (Number(b.amount) || 0), 0);

  return (
    <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Vehicle Photos &amp; Bills</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          What the acting driver recorded before and after the trip, and the expenses they raised.
        </p>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading photos and bills…</p>}
      {error && !isLoading && (
        <p className="text-sm text-red-600">Could not load the photos and bills for this trip.</p>
      )}

      {media && (
        <>
          <InspectionBlock title="Pre-trip photos" inspection={media.inspections?.pre} />
          <InspectionBlock title="Post-trip photos" inspection={media.inspections?.post} />

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h3 className="text-base font-medium text-gray-900">Bills &amp; Expenses</h3>
              {bills.length > 0 && (
                <span className="text-sm text-gray-600">Approved total: ₹{approvedTotal}</span>
              )}
            </div>
            {bills.length === 0 ? (
              <p className="text-sm text-gray-500">No bills were added.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-4 font-medium">Type</th>
                      <th className="py-2 pr-4 font-medium">Amount</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 pr-4 font-medium">Added</th>
                      <th className="py-2 pr-4 font-medium">Note</th>
                      <th className="py-2 font-medium">Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bills.map((bill, index) => (
                      <tr key={bill.billId || index} className="border-b border-gray-100 align-top">
                        <td className="py-2 pr-4 text-gray-900">{bill.type || '—'}</td>
                        <td className="py-2 pr-4 text-gray-900">₹{bill.amount}</td>
                        <td className="py-2 pr-4"><Badge badge={BILL_BADGE[bill.approval]} fallback={bill.approval} /></td>
                        <td className="py-2 pr-4 text-gray-600">{when(bill.addedAt)}</td>
                        <td className="py-2 pr-4 text-gray-600">{bill.note || '—'}</td>
                        <td className="py-2">
                          {bill.imageUrl ? (
                            <a href={bill.imageUrl} target="_blank" rel="noopener noreferrer" title="Open full size">
                              <img
                                src={bill.imageUrl}
                                alt="Bill receipt"
                                loading="lazy"
                                className="h-16 w-16 object-cover rounded-md border border-gray-200"
                              />
                            </a>
                          ) : (
                            <span className="text-gray-400">No image</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ActingTripMedia;
